import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFistRaised, faStar } from "@fortawesome/free-solid-svg-icons";

/**
 * Read-only fight card display for events that are not available for lineup submission
 * Shows simple fighter matchups - just names and who they're facing
 * Separates active fights from cancelled (hidden) fights
 * Shows win/loss badges when results are available
 */
const ReadOnlyFightCard = ({ fights }) => {
  if (!fights || fights.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FontAwesomeIcon
            icon={faFistRaised}
            className="text-gray-400 w-8 h-8"
          />
        </div>
        <h3 className="text-lg font-bold text-dark-grey mb-2">
          No Fights Available
        </h3>
        <p className="text-medium-grey">
          Fight card information will be available soon.
        </p>
      </div>
    );
  }

  // Helper function to determine if fighter won or lost
  const getFighterResult = (fighter) => {
    if (!fighter?.Outcome) return null;
    const outcome = fighter.Outcome.toLowerCase();
    if (outcome.includes("win")) return "win";
    if (outcome.includes("loss")) return "loss";
    return null;
  };

  // Separate active and cancelled fights
  const activeFights = fights.filter((fight) => !fight.hidden);
  const cancelledFights = fights.filter((fight) => fight.hidden);

  // Sort fights: main event first, then by order
  const sortedActiveFights = [...activeFights].sort((a, b) => {
    if (a.IsMainEvent && !b.IsMainEvent) return -1;
    if (!a.IsMainEvent && b.IsMainEvent) return 1;
    return (a.Order || 0) - (b.Order || 0);
  });

  const sortedCancelledFights = [...cancelledFights].sort(
    (a, b) => (a.Order || 0) - (b.Order || 0),
  );

  const renderFight = (fight, index) => {
    const fighter1 = fight.Fighters?.[0];
    const fighter2 = fight.Fighters?.[1];

    if (!fighter1 || !fighter2) return null;

    const fighter1Result = getFighterResult(fighter1);
    const fighter2Result = getFighterResult(fighter2);
    const hasResults = fighter1Result || fighter2Result;

    return (
      <div
        key={fight._id || index}
        className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-all duration-200 hover:shadow-md ${
          fight.IsMainEvent
            ? "border-deep-red ring-2 ring-deep-red/20"
            : "border-gray-100"
        }`}
      >
        {/* Main Event Badge */}
        {fight.IsMainEvent && (
          <div className="bg-gradient-to-r from-deep-red to-bright-red text-white px-4 py-2 text-xs font-bold flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faStar} className="w-3 h-3" />
            MAIN EVENT
          </div>
        )}

        <div className="p-4">
          {/* Fighter Matchup - Simple Display */}
          <div className="flex items-center justify-between gap-4">
            {/* Fighter 1 */}
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <div className="font-bold text-dark-grey text-lg">
                  {fighter1.FirstName} {fighter1.LastName}
                </div>
                {fighter1Result === "win" && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                    W
                  </span>
                )}
                {fighter1Result === "loss" && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                    L
                  </span>
                )}
              </div>
            </div>

            {/* VS Divider */}
            <div className="flex-shrink-0">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  hasResults ? "bg-gray-200" : "bg-gray-100"
                }`}
              >
                <span
                  className={`text-sm font-bold ${
                    hasResults ? "text-gray-700" : "text-gray-600"
                  }`}
                >
                  VS
                </span>
              </div>
            </div>

            {/* Fighter 2 */}
            <div className="flex-1 text-right">
              <div className="flex items-center justify-end gap-2">
                {fighter2Result === "win" && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                    W
                  </span>
                )}
                {fighter2Result === "loss" && (
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                    L
                  </span>
                )}
                <div className="font-bold text-dark-grey text-lg">
                  {fighter2.FirstName} {fighter2.LastName}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Active Fights */}
      {sortedActiveFights.length > 0 && (
        <div className="space-y-3">
          {sortedActiveFights.map((fight, index) => renderFight(fight, index))}
        </div>
      )}

      {/* Cancelled Fights Section */}
      {sortedCancelledFights.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-1">
            <div className="h-px flex-1 bg-gray-200"></div>
            <h3 className="text-sm font-bold text-medium-grey uppercase tracking-wide">
              Cancelled
            </h3>
            <div className="h-px flex-1 bg-gray-200"></div>
          </div>
          <div className="space-y-3 opacity-60">
            {sortedCancelledFights.map((fight, index) => (
              <div
                key={fight._id || `cancelled-${index}`}
                className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    {/* Fighter 1 */}
                    <div className="flex-1 text-left">
                      <div className="font-bold text-gray-500 text-lg">
                        {fight.Fighters?.[0]?.FirstName}{" "}
                        {fight.Fighters?.[0]?.LastName}
                      </div>
                    </div>

                    {/* VS Divider */}
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-sm font-bold text-gray-400">
                          VS
                        </span>
                      </div>
                    </div>

                    {/* Fighter 2 */}
                    <div className="flex-1 text-right">
                      <div className="font-bold text-gray-500 text-lg">
                        {fight.Fighters?.[1]?.FirstName}{" "}
                        {fight.Fighters?.[1]?.LastName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadOnlyFightCard;
